import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { adminAPI as sharedAdminAPI } from '../../services/api';
import { useOperationsAPI } from '../../context/useOperationsAPI';
import LoadingSpinner from '../../components/LoadingSpinner';
import PageLayout from '../../components/PageLayout';
import toast from 'react-hot-toast';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  TruckIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import {
  AdminEmptyState,
  AdminFilterCard,
  AdminPageHeader,
  AdminStatCard,
  AdminStatGrid,
  AdminTableCard,
} from '../../components/AdminSurface';
import InvoiceActionMenu from '../../components/invoices/InvoiceActionMenu';
import {
  VehicleCreateDrawer,
  VehicleEditDrawer,
} from '../../components/VehicleModals';

type VehicleStatus = 'available' | 'maintenance' | 'rented' | string;

type VehicleRecord = {
  id: string;
  fleet_number?: string | null;
  make?: string | null;
  model?: string | null;
  year?: number | null;
  color?: string | null;
  license_plate?: string | null;
  vin?: string | null;
  vehicle_type?: string | null;
  passenger_capacity?: number | null;
  current_mileage?: number | null;
  hourly_rate?: string | number | null;
  status?: VehicleStatus | null;
};

type VehicleFilters = {
  search: string;
  status: string;
  type: string;
};

function AdminVehicles() {
  return <AdminVehiclesContent />;
}

function labelize(value?: string | null) {
  return (value || 'N/A')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function formatMoney(value?: string | number | null) {
  const amount = Number(value || 0);
  return `$${(Number.isFinite(amount) ? amount : 0).toFixed(2)}`;
}

function formatNumber(value?: number | null) {
  return Number(value || 0).toLocaleString();
}

function vehicleSortValue(vehicle: VehicleRecord) {
  const fleetNumber = String(vehicle.fleet_number || '').trim();
  const numericFleet = Number(fleetNumber);
  if (Number.isFinite(numericFleet) && fleetNumber) return numericFleet;
  return Number.MAX_SAFE_INTEGER;
}

function statusBadgeClass(status?: VehicleStatus | null) {
  switch (status) {
    case 'available':
      return 'bg-success-container text-success-on-container';
    case 'rented':
      return 'bg-info-container text-info-on-container';
    case 'maintenance':
      return 'bg-warning-container text-warning-on-container';
    default:
      return 'bg-surface-container-low text-on-surface-variant';
  }
}

function statusIcon(status?: VehicleStatus | null) {
  switch (status) {
    case 'available':
      return <CheckCircleIcon className="h-3.5 w-3.5" />;
    case 'rented':
      return <TruckIcon className="h-3.5 w-3.5" />;
    case 'maintenance':
      return <WrenchScrewdriverIcon className="h-3.5 w-3.5" />;
    default:
      return <ExclamationTriangleIcon className="h-3.5 w-3.5" />;
  }
}

function StatusBadge({ status }: { status?: VehicleStatus | null }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${statusBadgeClass(
        status
      )}`}
    >
      {statusIcon(status)}
      {labelize(status)}
    </span>
  );
}

function AdminVehiclesContent() {
  const adminAPI = useOperationsAPI(sharedAdminAPI);
  const [filters, setFilters] = useState<VehicleFilters>({
    search: '',
    status: '',
    type: '',
  });
  const [selectedVehicle, setSelectedVehicle] = useState<VehicleRecord | null>(
    null
  );
  const [showCreateDrawer, setShowCreateDrawer] = useState(false);
  const [showEditDrawer, setShowEditDrawer] = useState(false);
  const [openActionMenuId, setOpenActionMenuId] = useState<string | null>(null);

  const queryClient = useQueryClient();

  const {
    data: vehiclesData,
    isLoading,
    isFetching,
    error,
  } = useQuery(
    ['admin-vehicles', filters.status],
    async () => {
      const response = await adminAPI.getAllVehicles?.({
        status: filters.status || undefined,
      });
      return Array.isArray(response?.data)
        ? (response.data as VehicleRecord[])
        : [];
    },
    { keepPreviousData: true }
  );

  const allVehicles = useMemo(() => {
    return [...(vehiclesData || [])].sort((a, b) => {
      const fleetDelta = vehicleSortValue(a) - vehicleSortValue(b);
      if (fleetDelta !== 0) return fleetDelta;
      return `${a.make || ''} ${a.model || ''}`.localeCompare(
        `${b.make || ''} ${b.model || ''}`
      );
    });
  }, [vehiclesData]);

  const vehicles = useMemo(() => {
    const search = filters.search.trim().toLowerCase();
    return allVehicles.filter((vehicle) => {
      const matchesSearch =
        !search ||
        [
          vehicle.fleet_number,
          vehicle.make,
          vehicle.model,
          vehicle.year,
          vehicle.color,
          vehicle.license_plate,
          vehicle.vin,
          vehicle.vehicle_type,
          vehicle.passenger_capacity,
        ].some((value) =>
          String(value || '')
            .toLowerCase()
            .includes(search)
        );
      const matchesType =
        !filters.type || vehicle.vehicle_type === filters.type;
      return matchesSearch && matchesType;
    });
  }, [allVehicles, filters.search, filters.type]);

  const summary = useMemo(() => {
    const countsByType = allVehicles.reduce<Record<string, number>>(
      (acc, vehicle) => {
        const type = vehicle.vehicle_type || 'unknown';
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      },
      {}
    );
    const largestType = Object.entries(countsByType).sort(
      (a, b) => b[1] - a[1]
    )[0];

    return {
      totalVehicles: allVehicles.length,
      availableVehicles: allVehicles.filter((v) => v.status === 'available')
        .length,
      inUseVehicles: allVehicles.filter((v) => v.status === 'rented').length,
      maintenanceVehicles: allVehicles.filter((v) => v.status === 'maintenance')
        .length,
      totalCapacity: allVehicles.reduce(
        (sum, vehicle) => sum + Number(vehicle.passenger_capacity || 0),
        0
      ),
      largestType,
    };
  }, [allVehicles]);

  useEffect(() => {
    const handleDocumentClick = () => setOpenActionMenuId(null);
    document.addEventListener('click', handleDocumentClick);
    return () => document.removeEventListener('click', handleDocumentClick);
  }, []);

  const deleteVehicleMutation = useMutation(
    (vehicleId: string) =>
      adminAPI.deleteVehicle?.(vehicleId) ||
      Promise.resolve({ data: { success: true } }),
    {
      onSuccess: () => {
        toast.success('Vehicle deleted');
        queryClient.invalidateQueries('admin-vehicles');
      },
      onError: (deleteError: any) => {
        toast.error(
          deleteError?.response?.data?.message || 'Failed to delete vehicle'
        );
      },
    }
  );

  const handleDeleteVehicle = (vehicle: VehicleRecord) => {
    const label = [
      vehicle.fleet_number ? `#${vehicle.fleet_number}` : null,
      vehicle.make,
      vehicle.model,
    ]
      .filter(Boolean)
      .join(' ');
    if (
      window.confirm(
        `Delete ${label || 'this vehicle'}? This action cannot be undone.`
      )
    ) {
      deleteVehicleMutation.mutate(vehicle.id);
    }
  };

  const handleEditVehicle = (vehicle: VehicleRecord) => {
    setSelectedVehicle(vehicle);
    setShowEditDrawer(true);
  };

  const clearFilters = () => {
    setFilters({ search: '', status: '', type: '' });
  };

  return (
    <PageLayout
      className="space-y-6"
      children={
        <>
          <AdminPageHeader
            eyebrow="Fleet"
            title="Vehicles"
            description="Manage active fleet records used for quoting, reservations, and vehicle availability planning."
            actions={
              <button
                type="button"
                onClick={() => setShowCreateDrawer(true)}
                className="btn-primary inline-flex items-center gap-2"
              >
                <PlusIcon className="h-5 w-5" />
                Add Vehicle
              </button>
            }
          />

          {error && (
            <div className="rounded-card border border-error/40 bg-error-container/20 px-4 py-3 text-sm text-on-surface">
              Failed to load vehicles.{' '}
              {(error as any)?.response?.data?.message ||
                (error as any)?.message ||
                'Unknown error'}
            </div>
          )}

          <AdminStatGrid>
            <AdminStatCard
              icon={<TruckIcon className="h-5 w-5" />}
              label="Total Vehicles"
              value={summary.totalVehicles}
              helper={`${summary.totalCapacity.toLocaleString()} passenger seats`}
              tone="primary"
            />
            <AdminStatCard
              icon={<CheckCircleIcon className="h-5 w-5" />}
              label="Available"
              value={summary.availableVehicles}
              helper={
                summary.totalVehicles
                  ? `${Math.round(
                      (summary.availableVehicles / summary.totalVehicles) * 100
                    )}% of fleet`
                  : 'No vehicles loaded'
              }
              tone="success"
            />
            <AdminStatCard
              icon={<TruckIcon className="h-5 w-5" />}
              label="In Use"
              value={summary.inUseVehicles}
              helper="Assigned or rented"
              tone="info"
            />
            <AdminStatCard
              icon={<WrenchScrewdriverIcon className="h-5 w-5" />}
              label="Maintenance"
              value={summary.maintenanceVehicles}
              helper={
                summary.largestType
                  ? `Most common: ${labelize(summary.largestType[0])}`
                  : 'No vehicle mix yet'
              }
              tone="warning"
            />
          </AdminStatGrid>

          <AdminFilterCard>
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(260px,1fr)_180px_180px_auto] lg:items-end">
              <div>
                <label className="mb-1 block text-sm font-medium text-on-surface-variant">
                  Search fleet
                </label>
                <div className="relative">
                  <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-on-surface-variant" />
                  <input
                    type="text"
                    className="input-field pl-9"
                    placeholder="Fleet #, plate, VIN, make, model..."
                    value={filters.search}
                    onChange={(event) =>
                      setFilters({ ...filters, search: event.target.value })
                    }
                  />
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-on-surface-variant">
                  Status
                </label>
                <select
                  className="input-field"
                  value={filters.status}
                  onChange={(event) =>
                    setFilters({ ...filters, status: event.target.value })
                  }
                >
                  <option value="">All statuses</option>
                  <option value="available">Available</option>
                  <option value="rented">In use</option>
                  <option value="maintenance">Maintenance</option>
                </select>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-on-surface-variant">
                  Type
                </label>
                <select
                  className="input-field"
                  value={filters.type}
                  onChange={(event) =>
                    setFilters({ ...filters, type: event.target.value })
                  }
                >
                  <option value="">All types</option>
                  <option value="sedan">Sedan</option>
                  <option value="suv">SUV</option>
                  <option value="van">Van</option>
                  <option value="sprinter">Sprinter</option>
                  <option value="minibus">Minibus</option>
                  <option value="motorcoach">Motorcoach</option>
                  <option value="truck">Truck</option>
                </select>
              </div>

              <button
                type="button"
                onClick={clearFilters}
                className="btn-secondary"
              >
                Clear
              </button>
            </div>
          </AdminFilterCard>

          <AdminTableCard
            title="Fleet roster"
            description="Dense operational view of vehicle identity, seating, rates, and current status."
            meta={
              <div className="flex flex-wrap items-center justify-end gap-2">
                {isFetching && !isLoading ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-outline/60 bg-surface-container-low px-2.5 py-1 text-xs font-medium text-on-surface-variant">
                    <LoadingSpinner size="sm" />
                    Refreshing
                  </span>
                ) : null}
                <span>
                  {vehicles.length} shown · {summary.totalVehicles} total
                </span>
              </div>
            }
          >
            <div className="overflow-x-auto">
              <table
                className={`admin-table-grid min-w-[64rem] ${
                  isFetching && !isLoading ? 'opacity-80' : ''
                }`}
                aria-busy={isFetching}
              >
                <thead className="admin-table-head">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-on-surface-variant">
                      Fleet
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-on-surface-variant">
                      Vehicle
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-on-surface-variant">
                      Plate / VIN
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-on-surface-variant">
                      Type
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-on-surface-variant">
                      Seats
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-on-surface-variant">
                      Mileage
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-on-surface-variant">
                      Hourly
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-on-surface-variant">
                      Status
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-on-surface-variant">
                      Actions
                    </th>
                  </tr>
                </thead>

                {isLoading ? (
                  <tbody className="admin-table-body">
                    {Array.from({ length: 8 }).map((_, index) => (
                      <tr
                        key={`vehicle-skeleton-${index}`}
                        className="admin-table-row animate-pulse"
                      >
                        <td className="px-4 py-3">
                          <div className="h-5 w-12 rounded bg-surface-container-low" />
                        </td>
                        <td className="px-4 py-3">
                          <div className="space-y-2">
                            <div className="h-4 w-32 rounded bg-surface-container-low" />
                            <div className="h-3 w-20 rounded bg-surface-container-low" />
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="h-4 w-36 rounded bg-surface-container-low" />
                        </td>
                        <td className="px-4 py-3">
                          <div className="h-6 w-20 rounded-full bg-surface-container-low" />
                        </td>
                        <td className="px-4 py-3">
                          <div className="ml-auto h-4 w-8 rounded bg-surface-container-low" />
                        </td>
                        <td className="px-4 py-3">
                          <div className="ml-auto h-4 w-14 rounded bg-surface-container-low" />
                        </td>
                        <td className="px-4 py-3">
                          <div className="ml-auto h-4 w-16 rounded bg-surface-container-low" />
                        </td>
                        <td className="px-4 py-3">
                          <div className="h-6 w-24 rounded-full bg-surface-container-low" />
                        </td>
                        <td className="px-4 py-3">
                          <div className="ml-auto h-7 w-16 rounded bg-surface-container-low" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                ) : vehicles.length > 0 ? (
                  <tbody className="admin-table-body">
                    {vehicles.map((vehicle) => {
                      const vehicleName =
                        [vehicle.make, vehicle.model]
                          .filter(Boolean)
                          .join(' ') || 'Vehicle';
                      const menuId = `vehicle-actions-${vehicle.id}`;

                      return (
                        <tr
                          key={vehicle.id}
                          className="admin-table-row cursor-pointer align-top"
                          onClick={() => handleEditVehicle(vehicle)}
                        >
                          <td className="px-4 py-3">
                            <span className="inline-flex min-w-[3rem] justify-center rounded-md bg-surface-container-low px-2.5 py-1 text-xs font-bold tabular-nums text-on-surface">
                              {vehicle.fleet_number
                                ? `#${vehicle.fleet_number}`
                                : 'N/A'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="min-w-[12rem]">
                              <p className="font-semibold text-on-surface">
                                {vehicleName}
                              </p>
                              <p className="text-xs text-on-surface-variant">
                                {[
                                  vehicle.year,
                                  vehicle.color
                                    ? labelize(vehicle.color)
                                    : null,
                                ]
                                  .filter(Boolean)
                                  .join(' · ') || 'No year/color'}
                              </p>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="min-w-[12rem]">
                              <p className="font-mono text-xs font-semibold text-on-surface">
                                {vehicle.license_plate || 'No plate'}
                              </p>
                              <p className="font-mono text-[11px] text-on-surface-variant">
                                {vehicle.vin || 'No VIN'}
                              </p>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex rounded-full bg-surface-container-low px-2.5 py-1 text-xs font-semibold text-on-surface">
                              {labelize(vehicle.vehicle_type)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right font-medium tabular-nums text-on-surface">
                            {vehicle.passenger_capacity || 0}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-on-surface-variant">
                            {formatNumber(vehicle.current_mileage)}
                          </td>
                          <td className="px-4 py-3 text-right font-medium tabular-nums text-on-surface">
                            {formatMoney(vehicle.hourly_rate)}
                          </td>
                          <td className="px-4 py-3">
                            <StatusBadge status={vehicle.status} />
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleEditVehicle(vehicle);
                                }}
                                className="admin-action-pill rounded-md bg-surface-container-low px-2.5 py-1 text-xs font-medium text-on-surface hover:bg-surface-container-highest"
                              >
                                Edit
                              </button>
                              <InvoiceActionMenu
                                menuId={menuId}
                                openMenuId={openActionMenuId}
                                setOpenMenuId={setOpenActionMenuId}
                                invoiceNumber={vehicleName}
                                actions={[
                                  {
                                    label: 'Edit vehicle',
                                    onClick: () => handleEditVehicle(vehicle),
                                  },
                                  {
                                    label: 'Delete vehicle',
                                    dangerous: true,
                                    onClick: () => handleDeleteVehicle(vehicle),
                                  },
                                ]}
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                ) : null}
              </table>
            </div>

            {!isLoading && vehicles.length === 0 && (
              <AdminEmptyState
                icon={<TruckIcon className="h-6 w-6" />}
                title="No vehicles found"
                description={
                  filters.search || filters.status || filters.type
                    ? 'Try adjusting your filters to see more fleet records.'
                    : 'Add vehicles to manage fleet availability for quotes and reservations.'
                }
                action={
                  <button
                    type="button"
                    onClick={() => setShowCreateDrawer(true)}
                    className="btn-primary inline-flex items-center gap-2"
                  >
                    <PlusIcon className="h-4 w-4" />
                    Add Vehicle
                  </button>
                }
              />
            )}
          </AdminTableCard>

          <VehicleCreateDrawer
            isOpen={showCreateDrawer}
            onClose={() => setShowCreateDrawer(false)}
          />

          <VehicleEditDrawer
            isOpen={showEditDrawer}
            onClose={() => {
              setShowEditDrawer(false);
              setSelectedVehicle(null);
            }}
            vehicle={selectedVehicle}
          />
        </>
      }
    />
  );
}

export default AdminVehicles;
